import { Injectable } from '@angular/core';
import { BaseService } from '../base/base.service';
import { Subscription, firstValueFrom } from 'rxjs';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { ArchiveDeleteComponent } from '../base/components/modal/archive-delete/archive-delete.component';
import { SnackbarService } from './snackbar.service';
import { Model } from '../models/model';

@Injectable({
  providedIn: 'root',
})
export class DependenciesService<T> {
  service: BaseService<T>;
  subscription = new Subscription();
  loading = false;
  dependencies: any;
  hasDependencies = false;
  isStepByStep = false;
  moduleName: string;
  moduleNameWithArticle: string;
  moduleNameTitleWithArticle: string;
  actionType: 'archive' | 'end-service' | 'update' | 'delete' = 'archive';
  private checkFn = 'checkDependencies';

  constructor(
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
  ) {}

  setActionType(type: 'archive' | 'end-service' | 'update' | 'delete') {
    this.actionType = type;
    return this;
  }

  setService(service: BaseService<any>) {
    this.service = service as BaseService<T>;
    return this;
  }

  setIsStepByStep(isStepByStep: boolean) {
    this.isStepByStep = isStepByStep;
    return this;
  }

  setModuleName(moduleName: string) {
    this.moduleName = moduleName;
    return this;
  }

  setModuleNameWithArticle(name: string) {
    this.moduleNameWithArticle = name;
    return this;
  }

  setModuleNameTitleWithArticle(name: string) {
    this.moduleNameTitleWithArticle = name;
    return this;
  }

  setCheckFunction(name: string) {
    this.checkFn = name;
    return this;
  }

  async check(model: Model): Promise<any> {
    this.loading = true;
    this.hasDependencies = false;

    try {
      const request = (this.service as any)[this.checkFn];
      const x: any = await firstValueFrom(request.call(this.service, model));
      this.dependencies = x.data;
      this.hasDependencies = this.dependencies.hasDependencies;
      return this.dependencies;
    } catch (error) {
      this.snackbarService.danger('Erreur lors de la vérification des dépendances');
      return null;
    } finally {
      this.loading = false;
    }
  }

  checkDependencies(model: Model, refresh: any, parentDialogRef?: MatDialogRef<any>): Promise<void> {
    return new Promise<void>(resolve => {
      const dialogRef = this.dialog.open<ArchiveDeleteComponent<T>>(ArchiveDeleteComponent, {
        data: {
          isStepByStep: this.isStepByStep,
          dependencies: this.check(model),
          defaults: model,
          name: this.moduleName,
          nameWithArticle: this.moduleNameWithArticle ?? this.moduleName,
          titleWithArticle: this.moduleNameTitleWithArticle ?? this.moduleNameWithArticle ?? this.moduleName,
          service: this.service,
          callback: () => this.check(model),
          actionType: this.actionType,
        },
      });

      dialogRef.afterClosed().subscribe(x => {
        if (x === 'reassigned') {
          parentDialogRef?.close();
          refresh(true);
        } else if (x === 'proceed-to-metrik') {
          // Pour end-service: les dépendances sont OK, on peut ouvrir la modale de metrik
          refresh(false);
        } else if (x) {
          parentDialogRef?.close();
          let successMessage = `Le ${this.moduleName} a été archivé`;
          if (this.actionType === 'end-service') {
            successMessage = `Le ${this.moduleName} a été mis hors service`;
          } else if (this.actionType === 'update') {
            successMessage = `Le ${this.moduleName} a été mis à jour`;
          } else if (this.actionType === 'delete') {
            successMessage = `Le ${this.moduleName} a été supprimé`;
          }
          this.snackbarService.success(successMessage);
          refresh(true);
        }
        resolve();
      });
    });
  }
}
